import { makeAutoObservable, observable } from "mobx";
import type { TProjectView, TProjectViewListFilters } from "@/types/view";

const defaultProjectViewListFilters = (): TProjectViewListFilters => ({
  search: "",
  sortBy: "default",
  favoritesOnly: false,
});

/**
 * A project's Views list, narrowed and ordered by its header's search, sort and
 * favourites-only controls.
 */
export function filterProjectViews(
  allViews: readonly TProjectView[],
  filters: TProjectViewListFilters,
): TProjectView[] {
  let views = [...allViews];

  if (filters.search.trim()) {
    const query = filters.search.trim().toLowerCase();
    views = views.filter(
      (view) =>
        view.name.toLowerCase().includes(query) || view.description.toLowerCase().includes(query),
    );
  }

  if (filters.favoritesOnly) {
    views = views.filter((view) => view.is_favorite);
  }

  views.sort((left, right) => {
    if (filters.sortBy === "name") {
      return left.name.localeCompare(right.name);
    }

    if (filters.sortBy === "created_at") {
      return right.created_at.localeCompare(left.created_at);
    }

    if (filters.sortBy === "updated_at") {
      return right.updated_at.localeCompare(left.updated_at);
    }

    if (left.is_favorite !== right.is_favorite) {
      return left.is_favorite ? -1 : 1;
    }

    return left.name.localeCompare(right.name);
  });

  return views;
}

/**
 * UI state for the saved-view pages: each project's Views list controls, kept
 * while the user moves between the list and a view. The views themselves are
 * server data, read through `hooks/use-views.ts` (NG-2807).
 */
export class ViewStore {
  listFiltersByProject = observable.map<string, TProjectViewListFilters>();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  getProjectListFilters(projectId: string): TProjectViewListFilters {
    return this.listFiltersByProject.get(projectId) ?? defaultProjectViewListFilters();
  }

  setProjectListFilters(projectId: string, value: Partial<TProjectViewListFilters>): void {
    this.listFiltersByProject.set(projectId, {
      ...this.getProjectListFilters(projectId),
      ...value,
    });
  }
}
